import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import HorizonRing from "@/components/HorizonRing";
import PostSignupShareModal from "@/components/PostSignupShareModal";
import CrossDisciplinePathCard from "@/components/CrossDisciplinePathCard";
import { useAuth } from "@/context/AuthContext";
import { BRANCH_META, BRANCH_ORDER } from "@/lib/branches";
import { Search, ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export default function AtlasMap() {
    const { user } = useAuth();
    const [disciplines, setDisciplines] = useState([]);
    const [filter, setFilter] = useState(null);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("name"); // name | progress | open_tasks

    useEffect(() => {
        api.get("/atlas/disciplines").then((r) => setDisciplines(r.data));
    }, []);

    const filtered = useMemo(() => {
        let list = disciplines;
        if (filter) list = list.filter((d) => d.branch === filter);
        if (q.trim()) {
            const s = q.trim().toLowerCase();
            list = list.filter(
                (d) => d.name.toLowerCase().includes(s) || d.blurb.toLowerCase().includes(s)
            );
        }
        list = [...list].sort((a, b) => {
            if (sort === "progress") return b.progress - a.progress;
            if (sort === "open_tasks") return b.open_tasks - a.open_tasks;
            return a.name.localeCompare(b.name);
        });
        return list;
    }, [disciplines, filter, q, sort]);

    const grouped = useMemo(() => {
        const g = {};
        BRANCH_ORDER.forEach((s) => { g[s] = []; });
        filtered.forEach((d) => {
            if (g[d.branch]) g[d.branch].push(d);
        });
        return g;
    }, [filtered]);

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <PostSignupShareModal user={user} />
            <div className="max-w-6xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="crystal rounded-3xl p-6 sm:p-8"
                >
                    <p className="chip" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}>
                        <Sparkles className="w-3 h-3" /> The Atlas of Human Endeavor
                    </p>
                    <h1
                        className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none"
                        data-testid="atlas-title"
                    >
                        {disciplines.length > 0 ? `${disciplines.length} disciplines. ` : ""}
                        Pick your frontier.
                    </h1>
                    <p className="text-slate-600 mt-2 max-w-2xl">
                        Every field humanity works on, listed and colour-coded. Search, filter,
                        and open any discipline to see its live workshop.
                    </p>

                    {/* Search + sort */}
                    <div className="mt-6 flex flex-col sm:flex-row gap-2">
                        <div className="flex-1 rounded-2xl border border-slate-200 bg-white flex items-center gap-2 px-4 py-2.5">
                            <Search className="w-4 h-4 text-slate-400" />
                            <input
                                className="bg-transparent outline-none w-full text-sm font-sans"
                                placeholder="Search 130+ disciplines…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                data-testid="atlas-search"
                            />
                        </div>
                        <select
                            className="field-input sm:w-52"
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            data-testid="atlas-sort"
                        >
                            <option value="name">Sort · Alphabetical</option>
                            <option value="progress">Sort · Most mapped</option>
                            <option value="open_tasks">Sort · Most open tasks</option>
                        </select>
                    </div>

                    {/* Branch pills */}
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <button
                            onClick={() => setFilter(null)}
                            className={`px-3 py-1.5 rounded-full text-xs font-display font-bold transition-all ${
                                !filter ? "bg-slate-900 text-cream" : "text-slate-700 hover:bg-white"
                            }`}
                            data-testid="filter-all"
                        >
                            All ({disciplines.length})
                        </button>
                        {BRANCH_ORDER.map((b) => {
                            const meta = BRANCH_META[b];
                            const active = filter === b;
                            const count = disciplines.filter((d) => d.branch === b).length;
                            return (
                                <button
                                    key={b}
                                    onClick={() => setFilter(active ? null : b)}
                                    className="px-3 py-1.5 rounded-full text-xs font-display font-bold transition-all flex items-center gap-1.5"
                                    style={{
                                        background: active ? meta.color : meta.soft,
                                        color: active ? (b === "arts" ? "#0F172A" : "#fff") : meta.text,
                                    }}
                                    data-testid={`filter-${b}`}
                                >
                                    <span
                                        className="w-2 h-2 rounded-full"
                                        style={{
                                            background: active ? "#fff" : meta.color,
                                            boxShadow: !active ? `0 0 6px ${meta.color}` : "none",
                                        }}
                                    />
                                    {meta.name} · {count}
                                </button>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Cross-Discipline Career Path — only renders when the user has beacons across 2+ disciplines */}
                {user && (
                    <div className="mt-6" data-testid="cross-discipline-section">
                        <CrossDisciplinePathCard />
                    </div>
                )}

                {/* Discipline list — grouped by branch when no branch filter */}
                {filter ? (
                    <DisciplineGrid items={filtered} />
                ) : (
                    <div className="mt-8 space-y-10">
                        {BRANCH_ORDER.map((b) => {
                            const items = grouped[b];
                            if (items.length === 0) return null;
                            const meta = BRANCH_META[b];
                            return (
                                <section key={b} data-testid={`section-${b}`}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <span
                                            className="w-3 h-3 rounded-full"
                                            style={{ background: meta.color, boxShadow: `0 0 12px ${meta.color}` }}
                                        />
                                        <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900">
                                            {meta.name}
                                        </h2>
                                        <span className="text-xs uppercase tracking-widest font-bold" style={{ color: meta.text }}>
                                            {items.length} disciplines
                                        </span>
                                    </div>
                                    <DisciplineGrid items={items} />
                                </section>
                            );
                        })}
                    </div>
                )}

                {filtered.length === 0 && (
                    <div className="mt-16 text-center text-slate-500 font-display">
                        No disciplines match that search. Try another word.
                    </div>
                )}
            </div>
        </div>
    );
}

function DisciplineGrid({ items }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((d) => {
                const meta = BRANCH_META[d.branch];
                return (
                    <Link
                        key={d.slug}
                        to={`/discipline/${d.slug}`}
                        className="crystal rounded-2xl p-4 hover:scale-[1.01] transition-transform group"
                        style={{ borderColor: meta.color }}
                        data-testid={`discipline-${d.slug}`}
                    >
                        <div className="flex items-start gap-3">
                            <HorizonRing size={54} stroke={7} pct={d.progress} color={meta.color} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span
                                        className="chip"
                                        style={{ background: meta.soft, color: meta.text }}
                                    >
                                        {meta.name}
                                    </span>
                                </div>
                                <h3 className="font-display font-bold text-lg text-slate-900 tracking-tight mt-1 truncate">
                                    {d.name}
                                </h3>
                                <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{d.blurb}</p>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="text-[11px] uppercase font-bold tracking-widest text-slate-400">
                                        {d.open_tasks} open tasks
                                    </span>
                                    <ArrowRight
                                        className="w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-transform"
                                    />
                                </div>
                            </div>
                        </div>
                    </Link>
                );
            })}
        </div>
    );
}
